import { InputRow } from './SchedulerView.types'
import { SchedulerResourceAndMaterialConstraintsRowType } from 'Features/Scheduler/Scheduler.types'

export type ConstraintRow = {
  id: number
  resource: string
  // Hourly keys: '<resource>-0', '<resource>-1', ..., '<resource>-<HOURS-1>'
  [hour: string]: unknown
}

const DEFAULT_HOURS = 168

/**
 * Build the per-resource hourly usage rows for the Constraints tab.
 *
 * Rows are seeded from `constraints` (the defined SOR resource/material
 * constraints) so that EVERY constrained resource appears as a row — even one
 * with zero usage in this schedule. Actual usage from each run's
 * `resource_requirement_kitchen` / `_line` is then overlaid. Any resource that
 * is used but not in the constraint list is still unioned in, so nothing
 * silently disappears.
 *
 * Previously rows were derived ONLY from used resources, so a constraint with
 * no usage never showed — making it impossible to tell whether it was the
 * binding constraint. (The sibling ScheduleResourceUsage.view.tsx already
 * iterates the constraint list; this brings the table in line with it.)
 *
 * Usage is production PLUS the draw of any changeover that holds a resource
 * while it runs — a bottle-format change naming `BTL01_BTL02`, say, which is
 * what stops the sibling line producing. Those hours have no production row,
 * so reading `resource_requirement_*` alone left the sibling line looking idle
 * for no reason.
 *
 * `hours` sets the width of each hourly array. Callers pass the schedule's
 * actual horizon (rounded up to whole days) so schedules running past hour 168
 * are no longer truncated.
 */
export const generateConstraintRows = (
  data: InputRow[],
  constraints: SchedulerResourceAndMaterialConstraintsRowType[] = [],
  hours: number = DEFAULT_HOURS
) => {
  const HOURS = Math.max(DEFAULT_HOURS, Math.floor(hours) || DEFAULT_HOURS)
  // Map each resource to an array of HOURS numbers (one per hour).
  const resourceMap: Record<string, number[]> = {}
  const resourceLocationMap: Record<string, string> = {}

  // Ensure a resource has an hourly array (and a location once we learn one).
  const ensureResource = (resource: string, location?: string) => {
    if (!resourceMap[resource]) {
      resourceMap[resource] = Array(HOURS).fill(0)
      resourceLocationMap[resource] = location ?? ''
    } else if (location && !resourceLocationMap[resource]) {
      resourceLocationMap[resource] = location
    }
  }

  // Seed a row per defined constraint so unused constraints still appear.
  constraints.forEach((c) => {
    if (c?.ResourceConstraint != null && String(c.ResourceConstraint) !== '') {
      ensureResource(String(c.ResourceConstraint))
    }
  })

  // Add a resource's contribution to a given time window.
  const addContribution = (
    resource: string,
    start: number,
    end: number,
    contribution: number
  ) => {
    if (!Number.isFinite(contribution)) return
    // Determine the whole-hour range. e.g. a window 2–4 contributes to hours 2 and 3.
    const startHour = Math.floor(start)
    const endHour = Math.ceil(end)
    for (let hour = startHour; hour < endHour && hour < HOURS; hour++) {
      if (hour >= 0) resourceMap[resource][hour] += contribution
    }
  }

  // resource_requirement_* may arrive as a JSON string or an object.
  //
  // The object check after JSON.parse is not belt-and-braces: the literal
  // string "null" parses SUCCESSFULLY to null, so the try/catch above it
  // never fires and `Object.keys(null)` throws, taking the whole tab out
  // through the error boundary. Rows carrying that string were written by a
  // mid-week reschedule before the API stopped serialising a null column with
  // `Value::to_string()`, and they are still in storage — a fix there cannot
  // clean them up, so this has to hold. Arrays and numbers parse cleanly too
  // and are equally not a requirement map.
  const parseReq = (
    req: unknown
  ): Record<string, { fixed: boolean; runRate: string | number }> => {
    if (!req) return {}
    if (typeof req === 'string') {
      try {
        const parsed = JSON.parse(req)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? parsed
          : {}
      } catch {
        return {}
      }
    }
    if (typeof req !== 'object' || Array.isArray(req)) return {}
    return req as Record<string, { fixed: boolean; runRate: string | number }>
  }

  // One entry of `additional_{line,kitchen}_changeovers` — the wire format
  // postprocess.rs::changeover_events_to_json writes. `resource` and
  // `resource_amount` are present only on an event that holds a resource for
  // its duration (a format change that names one), and are written together or
  // not at all. They are absent entirely on a schedule produced before the
  // engine reported them, which must read as "not known", never as zero.
  type ChangeoverEvent = {
    asset_id?: string
    start?: number
    end?: number
    resource?: string | null
    resource_amount?: number | null
  }

  const parseChangeovers = (raw: unknown): ChangeoverEvent[] => {
    if (!raw) return []
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }
    return Array.isArray(raw) ? (raw as ChangeoverEvent[]) : []
  }

  // One changeover is replicated onto every row of its block, so the same
  // event arrives here once per row. Count it once — keyed on the asset it
  // occupies and its window, within the asset (line or kitchen) it belongs to.
  const seenChangeovers = new Set<string>()

  // Changeover draws are accumulated per (asset, resource) as a per-hour MAX
  // and only added once every row has been read.
  //
  // One run can owe BOTH a format change and an equipment switch, and their
  // windows overlap by construction — both end where production begins. Naming
  // one resource on both is one occupancy of it: the line is being made ready
  // once, which is how the engine books it. Adding each event separately
  // reports 2.0 of a capacity-1 resource and draws the over-limit outline on a
  // schedule that is perfectly feasible. Draws on DIFFERENT assets still add —
  // those are two lines really holding the resource at once.
  const changeoverDraws = new Map<
    string,
    { resource: string; hours: number[] }
  >()

  const addChangeoverDraw = (
    raw: unknown,
    assetKey: string,
    location: string
  ) => {
    parseChangeovers(raw).forEach((event) => {
      const resource = event.resource
      const amount = Number(event.resource_amount)
      if (!resource || !Number.isFinite(amount) || amount <= 0) return
      const start = Number(event.start)
      const end = Number(event.end)
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start)
        return
      const key = `${assetKey}|${event.asset_id ?? ''}|${start}|${end}`
      if (seenChangeovers.has(key)) return
      seenChangeovers.add(key)
      ensureResource(resource, location)
      const drawKey = `${assetKey}|${resource}`
      let draw = changeoverDraws.get(drawKey)
      if (!draw) {
        draw = { resource, hours: Array(HOURS).fill(0) }
        changeoverDraws.set(drawKey, draw)
      }
      // Held flat for every hour of the change, exactly as the engine books it.
      for (let hour = Math.floor(start); hour < Math.ceil(end); hour++) {
        if (hour >= 0 && hour < HOURS) {
          draw.hours[hour] = Math.max(draw.hours[hour], amount)
        }
      }
    })
  }

  // Overlay actual usage from every scheduled run.
  data.forEach((row) => {
    // Kitchen resource requirements.
    const kitchenReq = parseReq(row.resource_requirement_kitchen)
    Object.keys(kitchenReq).forEach((resource) => {
      const resourceData = kitchenReq[resource]
      if (!resourceData) return
      const fixed = resourceData.fixed
      const runRate = Number(resourceData.runRate)
      const startHour = Math.floor(row.kitchen_start_time)
      const endHour = Math.ceil(row.kitchen_end_time)
      const hoursWindow = endHour - startHour
      // Fixed resources: contribution = runRate per hour.
      // Variable resources: distribute quantity * runRate evenly over the window.
      const contribution = fixed
        ? runRate
        : hoursWindow > 0
          ? (row.quantity * runRate) / hoursWindow
          : 0
      ensureResource(resource, row.site_location)
      addContribution(
        resource,
        row.kitchen_start_time,
        row.kitchen_end_time,
        contribution
      )
    })

    // Line resource requirements.
    const lineReq = parseReq(row.resource_requirement_line)
    Object.keys(lineReq).forEach((resource) => {
      const resourceData = lineReq[resource]
      if (!resourceData) return
      const fixed = resourceData.fixed
      const runRate = Number(resourceData.runRate)
      const startHour = Math.floor(row.line_start_time)
      const endHour = Math.ceil(row.line_end_time)
      const hoursWindow = endHour - startHour
      const contribution = fixed
        ? runRate
        : hoursWindow > 0
          ? (row.quantity * runRate) / hoursWindow
          : 0
      ensureResource(resource, row.site_location)
      addContribution(
        resource,
        row.line_start_time,
        row.line_end_time,
        contribution
      )
    })

    // Changeover draws. Reading the `additional_*` arrays alone is what keeps
    // this single-counted: they hold EVERY changeover event on the row,
    // including the one the scalar `line_changeover_start_time` fields repeat.
    addChangeoverDraw(
      row.additional_line_changeovers,
      row.line,
      row.site_location
    )
    addChangeoverDraw(
      row.additional_kitchen_changeovers,
      row.kitchen,
      row.site_location
    )
  })

  // Fold the per-asset maxima in, now that every row has contributed.
  changeoverDraws.forEach(({ resource, hours }) => {
    for (let hour = 0; hour < HOURS; hour++) {
      if (hours[hour] > 0) resourceMap[resource][hour] += hours[hour]
    }
  })

  const constraintRows: ConstraintRow[] = []
  Object.keys(resourceMap).forEach((resource, index) => {
    const rowObj: ConstraintRow = {
      id: index,
      resource,
      location: resourceLocationMap[resource],
    }
    resourceMap[resource].forEach((value, hour) => {
      // MUI datagrid throws on numeric keys, so prefix with the resource name.
      rowObj[`${resource}-${hour.toString()}`] = value
    })
    constraintRows.push(rowObj)
  })
  return constraintRows
}
